import {
  Icon,
  VerticalNavigation,
  type VerticalNavigationItem,
} from "@studio3t/generic-components";
import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useState,
  useTransition,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useGuardedLinkClick } from "@/components/LeaveGuard";
import { activeNavTo } from "@/utils/navRoute";
import { isPlainClick } from "@/utils/plainClick";
import { studioPath } from "@/utils/studioRoute";

export type NavItem = {
  to: string;
  label: string;
  icon: Icon;
  /** Match the route exactly (used for the index "/" route). */
  end?: boolean;
};

/**
 * Single source of truth for primary navigation. Consumed by the BridgeMenu
 * rail and by the command palette in App.tsx so the two never drift.
 */
export const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: Icon.Dashboard, end: true },
  { to: "/pipelines", label: "Pipelines", icon: Icon.Trace },
  { to: "/services", label: "Services", icon: Icon.Service },
  { to: "/studio", label: "Studio", icon: Icon.Run },
  { to: "/logs", label: "System logs", icon: Icon.Log },
  { to: "/audit-log", label: "Audit logs", icon: Icon.EventHistory },
];

// The wash moves on the click, the page follows in a transition. A page
// like the Studio — the explorer tree, the workbench editors, the AI
// Helper's conversation — takes well over 100ms to mount; rendered
// synchronously with the route change it starves the slide of frames and
// the wash just jumps. In a transition React renders the new page in
// slices while the old one stays up, so the frames keep coming; the rail
// marks the target active itself meanwhile, and drops that mark once the
// location has caught up. The leave guard keeps the first say: a click it
// holds behind its modal navigates on its own terms later.
function useEagerNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const guardedLink = useGuardedLinkClick();
  const [pendingTo, setPendingTo] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  // biome-ignore lint/correctness/useExhaustiveDependencies: the location settling is the trigger
  useEffect(() => setPendingTo(null), [location.pathname, location.search]);

  const onClick = (item: NavItem, target: string, e: ReactMouseEvent) => {
    guardedLink(target)(e);
    if (e.defaultPrevented || !isPlainClick(e)) return;
    e.preventDefault();
    setPendingTo(item.to);
    startTransition(() => navigate(target));
  };

  return { pendingTo, onClick };
}

// The rail itself is generic-components' VerticalNavigation (Figma
// Bridge_Menu 15777-120240, the sliding wash included). What stays here is
// the bridge's: the destinations, which one the location belongs to, the
// Studio's resolved path, and the guarded, eager navigation on a click.
export function BridgeMenu() {
  const location = useLocation();
  const { pendingTo, onClick } = useEagerNavigation();

  // The Studio tab resolves to the persisted New Studio choice up front,
  // so the click lands directly on the right route.
  const target = (item: NavItem) =>
    item.to === "/studio" ? studioPath() : item.to;
  const items: VerticalNavigationItem[] = NAV_ITEMS.map((item) => ({
    id: item.to,
    label: item.label,
    icon: item.icon,
    href: target(item),
  }));
  const activeId = pendingTo ?? activeNavTo(location.pathname, NAV_ITEMS);

  return (
    <Rail
      label="Primary"
      items={items}
      activeId={activeId}
      onChange={(id, e) => {
        const item = NAV_ITEMS.find((candidate) => candidate.to === id);
        if (item) onClick(item, target(item), e);
      }}
    />
  );
}

// The app's focus ring sits 2px inside every control (GlobalStyles), where
// on the rail the active wash and the icon paint over it and the active
// item shows no ring at all. Here it goes just outside the button instead;
// the rail's 8px inset leaves it room.
const Rail = styled(VerticalNavigation)`
  a,
  button {
    --focus-ring-offset: 1px;
  }
`;
